import React, { useState, useRef } from "react";
import axios from "axios";
import { useNavigate, useLocation } from "react-router-dom";
import mammoth from "mammoth";
import "./LegalApp.css";
import Navbar from "./Navbar";
import Footer from "./Footer";
import { FaFileWord } from "react-icons/fa";
import { LEGACY_FONTS, convertLegacyToUnicode, isLegacyFontSelected } from "../utils/legacyFontConverter";
import { createDocxFile } from "../utils/docxBuilder";

// Word extraction (mammoth) commonly leaves a blank line between paragraphs -
// strip those out so the review textarea shows compact text.
const removeEmptyLines = (text) =>
  (text || "")
    .split("\n")
    .filter((line) => line.trim() !== "")
    .join("\n");

const UploadCaseDiary = ({ API_URL }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const fir = location.state?.fir ?? null;

  const [language, setLanguage] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [legacyFont, setLegacyFont] = useState(LEGACY_FONTS[0]);
  const [caseDiaryText, setCaseDiaryText] = useState("");
  // Holds the last raw (un-converted) case diary text - straight from the
  // Word file extraction, or from the user's typing/pasting in the review
  // box - so switching the Legacy Font dropdown re-converts from that
  // original source instead of re-running a conversion on text that may
  // already be converted once.
  const rawCaseDiaryTextRef = useRef("");
  const [isParsingFile, setIsParsingFile] = useState(false);

  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  const [showOtherLanguagePopup, setShowOtherLanguagePopup] = useState(false);
  const [showConfirmationPopup, setShowConfirmationPopup] = useState(false);

  const wordFileInputRef = useRef(null);

  const handleCancel = (e) => {
    e.preventDefault();
    navigate(-1);
  };

  const handleLanguageChange = (event) => {
    if (event.target.value === "Other") {
      setShowOtherLanguagePopup(true);
    } else {
      setLanguage(event.target.value);
      setShowOtherLanguagePopup(false);
    }
  };

  const handleLanguageChangePopup = () => {
    setShowOtherLanguagePopup(false);
  };

  const handleDocxSelected = async (event) => {
    setErrorMessage(null);

    const file = event.target.files?.[0];
    if (!file) return;

    const isDocx =
      file.type ===
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
      file.name.toLowerCase().endsWith(".docx");

    if (!isDocx) {
      setSelectedFile(null);
      setErrorMessage("Please select a .docx Word document only.");
      if (wordFileInputRef.current) wordFileInputRef.current.value = "";
      return;
    }

    setSelectedFile(file);
    setCaseDiaryText("");
    rawCaseDiaryTextRef.current = "";
    setIsParsingFile(true);

    try {
      const arrayBuffer = await file.arrayBuffer();
      const result = await mammoth.extractRawText({ arrayBuffer });
      const extractedText = removeEmptyLines((result?.value || "").trim());

      if (!extractedText) {
        setErrorMessage("No readable text found in the selected Word document.");
        return;
      }

      rawCaseDiaryTextRef.current = extractedText;
      setCaseDiaryText(isLegacyFontSelected(legacyFont) ? convertLegacyToUnicode(extractedText, legacyFont) : extractedText);
    } catch (err) {
      console.error(err);
      setErrorMessage("Failed to read the Word document. Please try another file.");
    } finally {
      setIsParsingFile(false);
    }
  };

  const handleLegacyFontChange = (event) => {
    const newFont = event.target.value;
    setLegacyFont(newFont);

    if (isLegacyFontSelected(newFont) && rawCaseDiaryTextRef.current.trim() !== "") {
      setCaseDiaryText(convertLegacyToUnicode(rawCaseDiaryTextRef.current, newFont));
    } else if (newFont === LEGACY_FONTS[0]) {
      // Not Applicable - restore the original text as first extracted/pasted, undoing any conversion.
      setCaseDiaryText(rawCaseDiaryTextRef.current);
    }
  };

  const handleCaseDiaryTextChange = (event) => {
    setCaseDiaryText(event.target.value);
    rawCaseDiaryTextRef.current = event.target.value;
  };

  // Converts pasted text on the way in when a legacy font is selected - the
  // dropdown-driven reconversion only fires on a font *change*, so without
  // this a paste after the font was already selected would stay unconverted.
  const handleCaseDiaryTextPaste = (event) => {
    const pasted = (event.clipboardData || window.clipboardData).getData("text");
    if (!pasted) return;
    event.preventDefault();

    const textarea = event.target;
    const selectionStart = textarea.selectionStart;
    const selectionEnd = textarea.selectionEnd;

    const convertedPasted = isLegacyFontSelected(legacyFont) ? convertLegacyToUnicode(pasted, legacyFont) : pasted;
    const newDisplayed = caseDiaryText.slice(0, selectionStart) + convertedPasted + caseDiaryText.slice(selectionEnd);

    // Best-effort: mirror the same splice into the raw baseline using the
    // original (un-converted) pasted text, so a later font switch still
    // reconverts from the true source.
    const raw = rawCaseDiaryTextRef.current;
    const rawStart = Math.min(selectionStart, raw.length);
    const rawEnd = Math.min(selectionEnd, raw.length);
    rawCaseDiaryTextRef.current = raw.slice(0, rawStart) + pasted + raw.slice(rawEnd);

    setCaseDiaryText(newDisplayed);

    const newCursorPos = selectionStart + convertedPasted.length;
    setTimeout(() => textarea.setSelectionRange(newCursorPos, newCursorPos), 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (language === '') {
      setErrorMessage("Select the case diary language.");
      return;
    }
    if (selectedFile === null){
      setErrorMessage("Select the case diary file.");
      return;
    }
    if (!caseDiaryText.trim()) {
      setErrorMessage("The case diary content is empty. Please select a valid .docx file.");
      return;
    }

    setErrorMessage(null);
    setIsLoading(true);
    const jwt = localStorage.getItem("jwtToken");

    try {
      // Upload a freshly-built .docx containing the (possibly legacy-font
      // converted, possibly user-edited) reviewed text, instead of the
      // originally-selected file.
      const docxFile = await createDocxFile(caseDiaryText, selectedFile.name);

      const formData = new FormData();
      formData.append("id", fir?.id);
      formData.append("language", language);
      formData.append("file", docxFile, docxFile.name);

      // ✅ Change if your backend expects a different endpoint for case diary file upload
      const uri = "/fir/casediary";

      await axios.post(API_URL + uri, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${jwt}`,
        },
      });

      setShowConfirmationPopup(true);
    } catch (error) {
      const defaultErrorMessage =
        "Error processing request. Please try again later.";
      if (error.response?.status === 401) {
        setErrorMessage("Your session has expired. Please log in again.");
      } else {
        setErrorMessage(
          error.response?.data?.message || error.message || defaultErrorMessage
        );
      }
      console.error("Error submitting the form:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleWordFileClick = () => {
    if (language === "") {
      setErrorMessage("Please select the language to proceed.");
      return;
    }

    setErrorMessage(null);
    wordFileInputRef.current?.click();
  };

  return (
    <div>
      <Navbar />

      <div className="form-container" style={{ marginTop: "100px" }}>
        <div style={{ alignContent: "center", textAlign: "center" }}>
          <h2>Upload Case Diary</h2>
        </div>

        <br />

        <form
          className="responsive-form"
          onSubmit={handleSubmit}
          noValidate
          style={{
            width: "100%",
            display: "flex",
            flexDirection: "column",
            alignItems: "stretch",
            justifyContent: "flex-start",
          }}
        >
          <div style={{ width: "100%" }}>
            {/* Language + Legacy Font on the same row, wrapping on narrow widths */}
            <div
              style={{
                width: "100%",
                display: "flex",
                gap: "16px",
                flexWrap: "wrap",
                boxSizing: "border-box",
              }}
            >
              {/* Language */}
              <div className="form-group" style={{ flex: "1 1 200px" }}>
                <label htmlFor="document-type">Case Diary Language</label>
                <select
                  id="document-type"
                  name="document-type"
                  className="upload-select"
                  onChange={handleLanguageChange}
                  value={language}
                >
                  <option value="" disabled>
                    Select Language
                  </option>
                  <option value="English">English</option>
                  <option value="Hindi">Hindi</option>
                  <option value="Gujarati">Gujarati</option>
                  <option value="Marathi">Marathi</option>
                  <option value="Bengali">Bengali</option>
                  <option value="Odia">Odia</option>
                  <option value="Telugu">Telugu</option>
                  <option value="Tamil">Tamil</option>
                  <option value="Kannada">Kannada</option>
                  <option value="Malayalam">Malayalam</option>
                  <option value="Punjabi">Punjabi</option>
                  <option value="Urdu">Urdu</option>
                </select>
              </div>

              {/* Legacy Font */}
              <div className="form-group" style={{ flex: "1 1 200px" }}>
                <label htmlFor="legacy-font">Legacy Font</label>
                <select
                  id="legacy-font"
                  name="legacy-font"
                  className="upload-select"
                  value={legacyFont}
                  onChange={handleLegacyFontChange}
                >
                  {LEGACY_FONTS.map((font) => (
                    <option key={font} value={font}>{font}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* File selector - own full-width row so the Browse button always has room */}
            <div className="form-group" style={{ marginTop: "16px", width: "100%" }}>
              <label htmlFor="casediary-docx">Case Diary (.docx)</label>

              {/* Hidden real file input */}
              <input
                ref={wordFileInputRef}
                id="casediary-docx"
                type="file"
                accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                onChange={handleDocxSelected}
                disabled={isLoading || language === "Other"}
                style={{ display: "none" }}
              />

              {/* Visible "filename" input + Browse button */}
              <div style={{ display: "flex", gap: "10px", alignItems: "center", width: "100%", boxSizing: "border-box" }}>
                <input
                  type="text"
                  value={selectedFile ? selectedFile.name : ""}
                  readOnly
                  placeholder="No file selected"
                  disabled={isLoading || language === "Other"}
                  style={{
                    flex: "1 1 auto",
                    minWidth: 0,
                    height: "40px",
                    boxSizing: "border-box",
                    padding: "8px 10px",
                    border: "1px solid #ccc",
                    borderRadius: "6px",
                  }}
                />

                <button
                  type="button"
                  onClick={handleWordFileClick}
                  disabled={isLoading}
                  style={{
                    flex: "0 0 auto",
                    height: "40px",
                    padding: "0 14px",
                    borderRadius: "6px",
                    border: "1px solid #ccc",
                    background: "white",
                    cursor:
                      isLoading ? "not-allowed" : "pointer",
                    fontWeight: 600,
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    whiteSpace: "nowrap",
                  }}
                >
                  <FaFileWord style={{ fontSize: "18px", color: "#2B579A" }} />
                  Browse
                </button>
              </div>
            </div>

            {selectedFile && (
              <div className="form-group" style={{ position: "relative", marginTop: "16px" }}>
                <label htmlFor="casediary-review">Case Diary Content (review before submitting)</label>
                <textarea
                  id="casediary-review"
                  value={caseDiaryText}
                  onChange={handleCaseDiaryTextChange}
                  onPaste={handleCaseDiaryTextPaste}
                  disabled={isParsingFile}
                  style={{
                    width: "100%",
                    height: "300px",
                    marginTop: "10px",
                    fontSize: "16px",
                    lineHeight: "1.5",
                    resize: "vertical",
                    opacity: isParsingFile ? 0.5 : 1,
                    boxSizing: "border-box",
                  }}
                />

                {isParsingFile && (
                  <div
                    style={{
                      position: "absolute",
                      inset: 0,
                      backgroundColor: "rgba(255, 255, 255, 0.6)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      zIndex: 5,
                      borderRadius: "4px",
                    }}
                  >
                    <div style={{ textAlign: "center" }}>
                      <div className="spinner" />
                      <p style={{ marginTop: "8px", fontWeight: 600 }}>Reading Word document...</p>
                    </div>
                  </div>
                )}
              </div>
            )}

            <br />
            <br />

            {/* ✅ Submit + Cancel row (same layout as UploadChargeSheet) */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr auto 1fr",
                alignItems: "center",
                width: "100%",
                marginTop: "20px",
                columnGap: "12px",
              }}
            >
              {/* left spacer */}
              <div />

              {/* center submit */}
              <button
                type="submit"
                className="submit-button"
                style={{ fontWeight: "bold" }}
                disabled={isLoading || isParsingFile}
              >
                {isLoading ? <div className="spinner"></div> : "Submit"}
              </button>

              {/* right cancel */}
              <div style={{ justifySelf: "end" }}>
                <button
                  type="button"
                  onClick={handleCancel}
                  disabled={isLoading}
                  style={{
                    marginRight: "20px",
                    background: "transparent",
                    border: "none",
                    padding: 0,
                    fontWeight: 700,
                    cursor: isLoading ? "not-allowed" : "pointer",
                    textDecoration: "underline",
                    textUnderlineOffset: "4px",
                    lineHeight: 1,
                    color: "#000",
                    opacity: isLoading ? 0.6 : 1,
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>


            <div hidden={!isLoading} style={{ marginTop: "10px", textAlign: "center" }}>
              <p style={{ fontSize: "14px" }}>
                <strong>
                  Please wait. AI Agent is processing the request – this may take
                  about a minute depending on the complexity of the case diary.
                </strong>
              </p>
            </div>
          </div>
        </form>

        {errorMessage && (
          <div className="popup-overlay">
            <div className="popup-container">
              <h2>Error</h2>
              <div
                className="error-message"
                style={{ marginBottom: "15px", fontSize: "16px", color: "#333" }}
              >
                <strong>{errorMessage}</strong>
              </div>
              <button
                className="error-ok-button"
                onClick={() => setErrorMessage(null)}
                style={{
                  padding: "10px 20px",
                  fontSize: "14px",
                  color: "white",
                  backgroundColor: "#007bff",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                }}
              >
                OK
              </button>
            </div>
          </div>
        )}

        {showOtherLanguagePopup && (
          <div className="popup-overlay">
            <div className="popup-container">
              <h3>Support for more languages coming soon!</h3>
              <p>
                In the meantime, please translate the Case Diary into English using
                tools like{" "}
                <a
                  href="https://chat.openai.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  ChatGPT
                </a>{" "}
                and{" "}
                <a
                  href="https://openpdf.com/lp/translate-pdf.html"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  OpenPDF
                </a>{" "}
                and upload the translated case diary.
              </p>
              <p>
                <strong>For assistance, please email us at contact@legalapp.in.</strong>
              </p>
              <div className="popup-buttons">
                <button className="submit-popup-button" onClick={handleLanguageChangePopup}>
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {showConfirmationPopup && (
          <div className="popup-overlay">
            <div className="popup-container">
              <h3>Case Diary Submitted Successfully</h3>
              <p>The AI assistant is analyzing the Case Diary to generate the Charge Sheet.</p>
              <p>Please check the status after 15 minutes.</p>
              <p>You can continue using the app.</p>
              <div className="popup-buttons">
                <button
                  className="submit-popup-button"
                  onClick={() => navigate("/fir/dashboard", { state: { API_URL, fir } })}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default UploadCaseDiary;
